
import { useState } from "react";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { 
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  MessageSquare,
  Wallet,
  RefreshCw
} from "lucide-react";
import { CommissionRequest } from "@/types/commission.types";
import { formatCurrency } from "@/utils/formatters";
import { format, differenceInDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useUser } from "@/hooks/use-user";

interface StatusProps {
  request: CommissionRequest;
  onUpdateStatus: (newStatus: string) => void;
  isUpdating: boolean;
}

export function CommissionRequestStatus({ request, onUpdateStatus, isUpdating }: StatusProps) {
  const { user } = useUser();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [statusAction, setStatusAction] = useState<string | null>(null);
  
  const isArtist = user?.id === request.artist_id;
  const isClient = user?.id === request.client_id;
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500';
      case 'accepted': return 'bg-blue-500';
      case 'in_progress': return 'bg-purple-500';
      case 'revision': return 'bg-orange-500';
      case 'completed': return 'bg-green-500';
      case 'rejected': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="h-5 w-5" />;
      case 'accepted': return <CheckCircle className="h-5 w-5" />;
      case 'in_progress': return <Loader2 className="h-5 w-5" />;
      case 'revision': return <RefreshCw className="h-5 w-5" />;
      case 'completed': return <CheckCircle className="h-5 w-5" />;
      case 'rejected': return <XCircle className="h-5 w-5" />;
      default: return <Clock className="h-5 w-5" />;
    }
  };
  
  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Aguardando Aprovação';
      case 'accepted': return 'Aceito';
      case 'in_progress': return 'Em Progresso';
      case 'revision': return 'Em Revisão';
      case 'completed': return 'Concluído';
      case 'rejected': return 'Rejeitado';
      default: return status;
    }
  };
  
  const handleStatusChange = (newStatus: string) => {
    setStatusAction(newStatus);
    setIsDialogOpen(true);
  };
  
  const confirmStatusChange = () => {
    if (statusAction) {
      onUpdateStatus(statusAction);
    }
    setIsDialogOpen(false);
    setStatusAction(null);
  };
  
  const renderStatusActions = () => {
    if (isUpdating) return null;
    
    // Actions for the artist
    if (isArtist) {
      switch (request.status) {
        case 'pending':
          return (
            <div className="flex gap-2">
              <Button 
                variant="default" 
                size="sm" 
                onClick={() => handleStatusChange('accepted')}
              >
                Aceitar Comissão
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleStatusChange('rejected')}
              >
                Recusar
              </Button>
            </div>
          );
        case 'accepted':
          return (
            <Button 
              variant="default" 
              size="sm" 
              onClick={() => handleStatusChange('in_progress')}
            >
              Iniciar Trabalho
            </Button>
          );
        default:
          return null;
      }
    }
    
    // Actions for the client
    if (isClient) {
      switch (request.status) {
        case 'pending':
          return (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleStatusChange('canceled')}
            >
              Cancelar Solicitação
            </Button>
          );
        default:
          return null;
      }
    }
    
    return null;
  };
  
  const renderDeadlineInfo = () => {
    if (!request.delivery_deadline) return null;
    
    const deadline = new Date(request.delivery_deadline);
    const daysLeft = differenceInDays(deadline, new Date());
    const isPastDeadline = daysLeft < 0;
    
    return (
      <div className="mt-2 text-sm">
        <span className="text-muted-foreground">Data de entrega: </span>
        <span className={isPastDeadline ? 'text-red-500 font-medium' : 'font-medium'}>
          {format(deadline, 'dd/MM/yyyy', { locale: ptBR })}
          {isPastDeadline 
            ? ` (${Math.abs(daysLeft)} dias atrasado)`
            : ` (${daysLeft} dias restantes)`
          }
        </span>
      </div>
    );
  };

  return (
    <>
      <div className="mb-4">
        <h3 className="text-lg font-medium mb-2">Status da Comissão</h3>
        
        <div className="flex items-center gap-2 mb-2">
          <Badge 
            variant="outline" 
            className={`px-3 py-1 ${getStatusColor(request.status)} text-white`}
          >
            <span className="flex items-center gap-1">
              {getStatusIcon(request.status)}
              {getStatusText(request.status)}
            </span>
          </Badge>
        </div>
        
        {renderDeadlineInfo()}
        
        <div className="mt-4 bg-muted/50 rounded-md p-3">
          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">Preço:</span>
              <span className="font-medium">{formatCurrency(request.final_price_usd)} (Ξ{request.final_price_eth})</span>
            </div>
            
            {request.status === 'accepted' && (
              <Button size="sm" variant="outline" className="gap-1">
                <Wallet className="h-4 w-4" />
                Pagar
              </Button>
            )}
          </div>
        </div>
        
        <div className="mt-4">
          {renderStatusActions()}
        </div>
      </div>
      
      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar alteração de status</AlertDialogTitle>
            <AlertDialogDescription>
              {statusAction === 'accepted' && "Você está aceitando esta comissão. Confirma?"}
              {statusAction === 'rejected' && "Você está recusando esta comissão. Confirma?"}
              {statusAction === 'in_progress' && "Você está iniciando o trabalho nesta comissão. Confirma?"}
              {statusAction === 'canceled' && "Você está cancelando esta solicitação de comissão. Confirma?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmStatusChange}>
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
